import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SPACE_FOLDER_PAGE_SIZE, SpaceKind, SpaceNodeSortBy, SpaceSearchView } from 'shared';

import { ApiService } from '../../../../services/index';
import { Order } from '../../../../types/general.types';
import { PageRoute } from '../../../../types/page.types';
import { SPACE_BACKEND_ROUTE } from '../../constants/spaces-query.const';
import { useSpacesHome } from '../../hooks/use-spaces-home.hook';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { SpaceBrowserCrumb } from '../../types/data-room.types';
import { foundFolderBreadcrumbs } from '../../utils/space-browser.util';
import { toSelectedSpace } from '../../utils/space-list-item.util';

type RoomFolder = { dataRoomId: string; folderId: string; folderName: string };

/**
 * Opens a folder of a data room in Workspaces, where an Activity row names it.
 *
 * A row knows the folder and not the path down to it, and Workspaces opens a folder by its path.
 * The room's own search answers a folder with its ancestors, so the folder is looked up by name and
 * picked out by id. A folder the search does not return — the room's root, or one renamed since —
 * opens the room instead, as does a search that fails.
 */
export const useOpenRoomFolder = (): ((folder: RoomFolder) => Promise<void>) => {
  const navigate = useNavigate();
  const reveal = SidebarTreeStore((state) => state.reveal);
  const { home } = useSpacesHome();

  return useCallback(
    async ({ dataRoomId, folderId, folderName }: RoomFolder): Promise<void> => {
      const room = home?.dataRooms.find(({ id }) => id === dataRoomId);

      if (room) {
        const breadcrumbs = await ApiService.get<SpaceSearchView>(
          { endpoint: SPACE_BACKEND_ROUTE[SpaceKind.DATA_ROOM], routePath: [dataRoomId, 'search'] },
          {
            queryParams: {
              search: folderName,
              skip: 0,
              sortBy: SpaceNodeSortBy.NAME,
              sortOrder: Order.ASC,
              take: SPACE_FOLDER_PAGE_SIZE,
            },
          },
        )
          .then(({ folders }): null | SpaceBrowserCrumb[] => {
            const found = folders.find(({ id }) => id === folderId);

            return found ? foundFolderBreadcrumbs(found) : null;
          })
          .catch(() => null);

        reveal({
          breadcrumbs: breadcrumbs ?? [],
          folderId: breadcrumbs ? folderId : null,
          space: toSelectedSpace(room),
        });
      }

      navigate(PageRoute.DATA_ROOM_WORKSPACES);
    },
    [home, navigate, reveal],
  );
};
